export const REALTIME_STATES = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONCILING: 'reconciling',
  OFFLINE: 'offline',
  BACKGROUND: 'background',
  DISCONNECTED_AUTH: 'disconnected-auth',
  STOPPED: 'stopped',
});

export const FOREGROUND_RECOVERY_DEDUP_WINDOW_MS = 900;

const activeLifecycle = (value) => value === 'active';

/**
 * Owns lifecycle/network-triggered realtime recovery for one authenticated
 * application session. It deliberately does not own Socket.IO transport
 * backoff; the socket service remains the sole transport owner.
 */
export function createRealtimeSessionCoordinator({
  lifecycle,
  network,
  reconcile,
  onBackground = () => {},
  onOffline = () => {},
  onOnline = () => {},
  onStateChange = () => {},
} = {}) {
  let state = REALTIME_STATES.IDLE;
  let lifecycleState = lifecycle?.getState?.() || 'active';
  let networkState = network?.getState?.() || { connected: true, connectionType: 'unknown' };
  let session = null;
  let sessionGeneration = 0;
  let inFlight = null;
  let pendingReason = null;
  let started = false;
  let stopped = false;
  let unsubscribeLifecycle = null;
  let unsubscribeNetwork = null;
  let lifecycleRefreshSequence = 0;
  let networkRevision = 0;
  let lastForegroundRecoveryAt = 0;

  const publish = (next, detail = {}) => {
    if (state === next && !detail.force) return;
    state = next;
    onStateChange({ state, sessionGeneration, ...detail });
  };

  const current = (generation) => (
    !stopped &&
    generation === sessionGeneration &&
    Boolean(session?.userId && session?.token)
  );

  const request = (reason = 'unspecified') => {
    if (stopped || !session?.userId || !session?.token) {
      publish(stopped ? REALTIME_STATES.STOPPED : REALTIME_STATES.DISCONNECTED_AUTH);
      return Promise.resolve(false);
    }
    if (!networkState.connected) {
      pendingReason = reason;
      publish(REALTIME_STATES.OFFLINE, { reason });
      return Promise.resolve(false);
    }
    if (!activeLifecycle(lifecycleState)) {
      pendingReason = reason;
      publish(REALTIME_STATES.BACKGROUND, { reason });
      return Promise.resolve(false);
    }
    if (inFlight?.generation === sessionGeneration) return inFlight.promise;

    const generation = sessionGeneration;
    const context = Object.freeze({
      generation,
      reason,
      userId: session.userId,
      authGeneration: session.authGeneration,
      isCurrent: () => current(generation),
    });
    pendingReason = null;
    publish(REALTIME_STATES.RECONCILING, { reason });
    const promise = Promise.resolve()
      .then(() => reconcile(context))
      .then((result) => {
        if (!current(generation)) return false;
        publish(result === false ? REALTIME_STATES.IDLE : REALTIME_STATES.CONNECTED, { reason });
        return result !== false;
      })
      .catch(() => {
        if (current(generation)) publish(REALTIME_STATES.IDLE, { reason });
        return false;
      })
      .finally(() => {
        if (inFlight?.generation === generation) inFlight = null;
      });
    inFlight = { generation, promise };
    return promise;
  };

  const onLifecycle = async ({ state: next, source } = {}) => {
    const wasActive = activeLifecycle(lifecycleState);
    lifecycleState = next;
    if (!activeLifecycle(next)) {
      lifecycleRefreshSequence += 1;
      onBackground();
      publish(REALTIME_STATES.BACKGROUND, { reason: 'lifecycle-background' });
      return;
    }
    const isPageShow = next === 'active' && source === 'pageshow';
    if (wasActive && !pendingReason && !isPageShow) return;
    if (activeLifecycle(next)) {
      const now = Date.now();
      if (now - lastForegroundRecoveryAt < FOREGROUND_RECOVERY_DEDUP_WINDOW_MS) return;
      lastForegroundRecoveryAt = now;
    }

    // Android may deliver appStateChange before the queued Network callback.
    // Read current platform authority before allowing foreground recovery so
    // a stale cached `connected=true` cannot briefly reopen Socket.IO offline.
    const refreshSequence = ++lifecycleRefreshSequence;
    const revisionAtStart = networkRevision;
    try {
      const refreshed = await network?.refresh?.();
      if (refreshSequence !== lifecycleRefreshSequence || !activeLifecycle(lifecycleState)) return;
      if (refreshed && revisionAtStart === networkRevision) onNetwork(refreshed);
    } catch {}
    if (refreshSequence !== lifecycleRefreshSequence || !activeLifecycle(lifecycleState)) return;
    if (networkState.connected) onOnline();
    void request('lifecycle-active');
  };

  const onNetwork = (next = {}) => {
    networkRevision += 1;
    const previous = networkState;
    networkState = next;
    if (!next.connected) {
      onOffline();
      pendingReason = 'network-disconnected';
      publish(REALTIME_STATES.OFFLINE, { reason: 'network-disconnected' });
      return;
    }
    const restored = previous?.connected === false;
    // A connected Wi-Fi/cellular label change is not transport failure. The
    // singleton's Socket.IO transport will emit `connect` if the handoff
    // actually replaces it; avoid a second application reconciliation when
    // Android reports the new connection type after connectivity restoration.
    if (restored || pendingReason) {
      if (restored && activeLifecycle(lifecycleState)) onOnline();
      void request('network-restored');
    }
  };

  return {
    start() {
      if (started) return;
      started = true;
      stopped = false;
      unsubscribeLifecycle = lifecycle?.subscribe?.(onLifecycle) || null;
      unsubscribeNetwork = network?.subscribe?.(onNetwork) || null;
    },
    stop() {
      if (stopped) return;
      stopped = true;
      started = false;
      sessionGeneration += 1;
      session = null;
      inFlight = null;
      lastForegroundRecoveryAt = 0;
      pendingReason = null;
      lifecycleRefreshSequence += 1;
      unsubscribeLifecycle?.();
      unsubscribeNetwork?.();
      unsubscribeLifecycle = null;
      unsubscribeNetwork = null;
      publish(REALTIME_STATES.STOPPED);
    },
    setSession({ userId, token, authGeneration = 0 } = {}) {
      const nextUserId = String(userId || '').trim();
      if (!nextUserId || !token) return this.clearSession();
      const changed = !session || session.userId !== nextUserId || session.authGeneration !== authGeneration;
      session = { userId: nextUserId, token, authGeneration };
      if (changed) {
        sessionGeneration += 1;
        inFlight = null;
      }
      stopped = false;
      return request(changed ? 'auth-session' : 'token-authority-updated');
    },
    clearSession() {
      sessionGeneration += 1;
      session = null;
      inFlight = null;
      pendingReason = null;
      lastForegroundRecoveryAt = 0;
      publish(REALTIME_STATES.DISCONNECTED_AUTH);
      return Promise.resolve(false);
    },
    request,
    getState: () => state,
    getSessionGeneration: () => sessionGeneration,
    getSnapshot: () => Object.freeze({ state, lifecycleState, networkState, sessionGeneration, hasSession: Boolean(session) }),
  };
}
